import editionsJson from "./newsEditions.json";
import eventsJson from "./newsEvents.json";
import sourcesJson from "./newsSources.json";

export type NewsTreatment = "video-spot" | "glance";
export type NewsDecision = "keep" | "keep-selective" | "watch-selective" | "glance-only";
export type NewsTopic = "world" | "china" | "business" | "tech" | "science" | "health" | "culture";

// Geopolitics may use only these lanes. Russia–Europe also needs consequential: true.
export const worldOrderLanes = [
  "us-china",
  "ukraine-russia",
  "iran-us",
  "us-continuity",
  "china-continuity",
  "russia-europe",
] as const;

export type WorldOrderLane = (typeof worldOrderLanes)[number];

export const worldOrderLaneNotes: Record<WorldOrderLane, string> = {
  "us-china": "US–China policy, technology, or security.",
  "ukraine-russia": "A shift in the Ukraine–Russia war.",
  "iran-us": "Iran–US.",
  "us-continuity": "A major US continuity signal.",
  "china-continuity": "A major China continuity signal.",
  "russia-europe": "Russia–Europe only when it moves the board.",
};

export type NewsSourceLink = {
  name: string;
  url: string;
  favorite?: boolean;
};

export type NewsVideo = {
  src: string;
  poster?: string;
  duration?: string;
};

type WorldOrderFields = {
  worldOrder?: WorldOrderLane;
  consequential?: boolean;
};

export type NewsTopItem = WorldOrderFields & {
  id: string;
  kind: "video" | "read";
  topic: NewsTopic;
  minutes: number;
  title: string;
  points: string[];
  more: string[];
  video?: NewsVideo;
  source: NewsSourceLink;
};

export type NewsGlanceItem = WorldOrderFields & {
  id: string;
  topic: NewsTopic;
  minutes: number;
  title: string;
  points: string[];
  source: NewsSourceLink;
};

// Same object a future X ingest should write. The site does not call the X API.
export type NewsAnnouncement = {
  id: string;
  handle: string;
  url: string;
  text: string;
  why: string;
};

export type NewsEdition = {
  date: string;
  top: NewsTopItem[];
  glance: NewsGlanceItem[];
  announcements?: NewsAnnouncement[];
};

export type NewsEventRank = "board" | "tour";
export type NewsEventKind = "lab" | "policy" | "summit" | "earnings" | "platform";

export type NewsOfficialEvent = {
  id: string;
  date: string;
  dateLabel?: string;
  name: string;
  where: string;
  why: string;
  url: string;
  kind: NewsEventKind;
  rank: NewsEventRank;
};

export type NewsDirectorySource = {
  id: string;
  name: string;
  platform: "youtube" | "bilibili";
  url: string;
  niche: string;
  cluster: string;
  decision: NewsDecision;
  treatment: NewsTreatment;
  why: string;
  gate?: string;
  priority?: number;
  origin: "roster" | "seed";
};

type NewsSourceDirectory = {
  audit: string;
  skipped: number;
  sources: NewsDirectorySource[];
};

// Reader time is the scarce resource: three is the format, five is the ceiling for glances.
export const newsLimits = { top: 3, videos: 3, points: 3, glance: 5, announcements: 5, events: 5 } as const;

export const isWorldOrderLane = (value: string | undefined): value is WorldOrderLane =>
  Boolean(value && (worldOrderLanes as readonly string[]).includes(value));

// Topic "world" is geopolitics. Anything else on that topic is dropped.
export const passesFeedFilter = (item: { topic: NewsTopic; worldOrder?: string; consequential?: boolean }) => {
  if (item.topic !== "world") return true;
  if (!isWorldOrderLane(item.worldOrder)) return false;
  if (item.worldOrder === "russia-europe" && item.consequential !== true) return false;
  return true;
};

export const topicLabels: Record<NewsTopic, string> = {
  world: "World",
  china: "China",
  business: "Business",
  tech: "Tech & AI",
  science: "Science",
  health: "Health",
  culture: "Culture",
};

export const weeklyTopics = Object.keys(topicLabels) as NewsTopic[];

export const platformLabels: Record<NewsDirectorySource["platform"], string> = {
  youtube: "YouTube",
  bilibili: "Bilibili",
};

export const decisionLabels: Record<NewsDecision, string> = {
  keep: "Keep",
  "keep-selective": "Keep selective",
  "watch-selective": "Watch selective",
  "glance-only": "Glance only",
};

export const treatmentGroups: { treatment: NewsTreatment; title: string; description: string }[] = [
  { treatment: "video-spot", title: "Video spot", description: "Can take the day’s video slot. One a day at most." },
  { treatment: "glance", title: "Glance", description: "A line or a transcript first. Never a full slot." },
];

export const nicheLabel = (niche: string) => niche.replace(/-/g, " ");

const clampPoints = <T extends { points: string[] }>(item: T): T => ({
  ...item,
  points: item.points.slice(0, newsLimits.points),
});

export const shapeAnnouncements = (items: NewsAnnouncement[] | undefined) =>
  (items ?? [])
    .filter((item) => item.id && item.handle && item.text && item.why && /^https:\/\//.test(item.url))
    .slice(0, newsLimits.announcements);

const byEventDate = (left: NewsOfficialEvent, right: NewsOfficialEvent) =>
  left.date < right.date ? -1 : left.date > right.date ? 1 : left.name.localeCompare(right.name);

// Board dates (labs, policy, summits, earnings) fill the strip first. Tour stops take leftover slots, then the strip is ordered by date.
export const officialEventsFor = (asOf: string, limit = newsLimits.events) => {
  const upcoming = (eventsJson as NewsOfficialEvent[]).filter((event) => event.date >= asOf).sort(byEventDate);
  const board = upcoming.filter((event) => event.rank !== "tour");
  const tour = upcoming.filter((event) => event.rank === "tour");
  return [...board, ...tour].slice(0, limit).sort(byEventDate);
};

export const formatEventDate = (event: NewsOfficialEvent) => {
  if (event.dateLabel) return event.dateLabel;
  const [year, month, day] = event.date.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const shapeEdition = (edition: NewsEdition): NewsEdition => {
  const favoritesFirst = edition.top.filter(passesFeedFilter).sort(
    (left, right) => Number(Boolean(right.source.favorite)) - Number(Boolean(left.source.favorite)),
  );
  let videos = 0;
  const top = favoritesFirst.slice(0, newsLimits.top).map((item) => {
    const video = item.video && videos < newsLimits.videos ? item.video : undefined;
    if (video) videos += 1;
    return { ...clampPoints(item), video };
  });
  const glance = edition.glance.filter(passesFeedFilter).slice(0, newsLimits.glance).map(clampPoints);
  return { ...edition, top, glance, announcements: shapeAnnouncements(edition.announcements) };
};

export const editions = (editionsJson as NewsEdition[])
  .map(shapeEdition)
  .sort((left, right) => (left.date < right.date ? 1 : left.date > right.date ? -1 : 0));

const directory = sourcesJson as NewsSourceDirectory;
export const directoryAudit = directory.audit;
export const skippedSourceCount = directory.skipped;
export const directorySources = directory.sources;
export const sourcesByTreatment = (treatment: NewsTreatment) =>
  directorySources.filter((source) => source.treatment === treatment);

export const isEditionDate = (value: string | undefined): value is string =>
  Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value));

export const latestEdition = () => editions[0];

export const editionByDate = (date: string | undefined) =>
  isEditionDate(date) ? editions.find((edition) => edition.date === date) : undefined;

export const editionStats = (edition: NewsEdition) => {
  const sources = new Set([...edition.top, ...edition.glance].map((item) => item.source.name));
  const minutes = [...edition.top, ...edition.glance].reduce((total, item) => total + item.minutes, 0);
  return {
    minutes,
    sources: sources.size,
    videos: edition.top.filter((item) => item.video).length,
  };
};

const weekStart = (date: string) => {
  const day = new Date(`${date}T00:00:00Z`);
  const offset = (day.getUTCDay() + 6) % 7;
  day.setUTCDate(day.getUTCDate() - offset);
  return day.toISOString().slice(0, 10);
};

export const weeklyCoverage = (edition: NewsEdition) => {
  const start = weekStart(edition.date);
  const covered = new Set<NewsTopic>();
  editions
    .filter((item) => item.date <= edition.date && weekStart(item.date) === start)
    .forEach((item) => [...item.top, ...item.glance].forEach((entry) => covered.add(entry.topic)));
  return weeklyTopics.map((topic) => ({ topic, label: topicLabels[topic], covered: covered.has(topic) }));
};

export const plainText = (text: string) => text.replace(/==([^=]+)==/g, "$1");
